import React, { useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { formatFileSize } from '../../../utils/formatFileSize';
import { getAllFilesTotalSize } from '../../../utils/helper';

const MAX_NUMBER_OF_FILES = 3;

const ALLOWED_MIME_TYPES = [
  'text/csv',
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
];

const MultipleFileUpload = ({ onUpload, onRawFileUpload, maxTotalSize }) => {
  const [files, setFiles] = useState([]);
  const dispatch = useDispatch();

  const handleFiles = (selectedFiles) => {
    let current = [...files];

    for (let file of selectedFiles) {
      if (current.length >= MAX_NUMBER_OF_FILES) {
        dispatch(showSnackbar({ message: 'Maximum 3 files allowed', severity: 'error' }));
        break;
      }

      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        dispatch(showSnackbar({ message: `Unsupported format: ${file.type}`, severity: 'error' }));
        continue;
      }

      if (getAllFilesTotalSize(current) + file.size > maxTotalSize) {
        dispatch(showSnackbar({ message: `Total Size exceeds ${formatFileSize(maxTotalSize)}`, severity: 'error' }));
        break;
      }
      current.push(file);
    }

    setFiles(current);
    processFiles(current);
  };

  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = () => reject(reader.error);
    });
  };
  const processFiles = async (fileList) => {
    if (!onUpload) return;

    const formatted = await Promise.all(
      fileList.map(async (file) => ({
        file_name: file.name,
        file_type: file.type,
        file_content: await toBase64(file),
      })),
    );

    onUpload(formatted);
    onRawFileUpload(fileList);
  };

  const handleRemove = (index) => {
    const updated = files.filter((_, idx) => idx !== index);
    setFiles(updated);
    processFiles(updated);
  };

  const handleFileChange = (e) => {
    handleFiles(Array.from(e.target.files));
  };

  const onDrop = (event) => {
    event.preventDefault();
    handleFiles(Array.from(event.dataTransfer.files));
  };

  return (
    <Box>
      {files.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', border: '1px solid #ccc', borderRadius: 1 }}>
          {files.map((file, index) => (
            <Box key={index} sx={{ borderBottom: '1px solid #ccc', px: 1, py: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon fontSize="small" color="black" />
                  <Typography variant="body2">{file.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">{formatFileSize(file.size)}</Typography>
                  <IconButton variant="body2" onClick={() => handleRemove(index)}>
                    <CloseIcon color="grey" size="small" fontWeight="bold" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        style={{
          border: '2px dashed #ccc',
          borderRadius: '8px',
          textAlign: 'center',
          padding: '20px',
          backgroundColor: '#fafafa',
          marginTop: '1rem',
        }}
      >
        <CloudUpload fontSize="large" color="action" />
        <Typography variant="body2" mt={1}>
          Drag & Drop files here or Click to browse
        </Typography>
        <Typography variant="caption" color="textSecondary">
          PDF, Images, Office documents. Max files: 3
        </Typography>

        <Box mt={2}>
          <input
            type="file"
            multiple
            style={{ display: 'none' }}
            id="file-input"
            onChange={(e) => handleFileChange(e)}
          />
          <label htmlFor="file-input">
            <Button variant="contained" color="primary" component="span" size="small">
              Browse
            </Button>
          </label>
        </Box>
      </Box>
    </Box>
  );
};

export default MultipleFileUpload;
